import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useState } from 'react';
import * as styles from '../../styles/internal-page/debug-viewer.module.scss';
import DebugJsonTree from './DebugJsonTree';

const TABS = [
  { key: 'summary', label: 'Résumé' },
  { key: 'page', label: 'Page' },
  { key: 'navigation', label: 'Navigation' },
  { key: 'logs', label: 'Logs' },
  { key: 'network', label: 'Réseau' },
];

const BROWSER_PATTERNS = [
  { name: 'Edge', regex: /Edg\/([\d.]+)/ },
  { name: 'Opera', regex: /OPR\/([\d.]+)/ },
  { name: 'Chrome', regex: /Chrome\/([\d.]+)/ },
  { name: 'Firefox', regex: /Firefox\/([\d.]+)/ },
  { name: 'Safari', regex: /Version\/([\d.]+).*Safari/ },
];

const OS_PATTERNS = [
  { name: 'Windows', regex: /Windows NT ([\d.]+)/ },
  { name: 'macOS', regex: /Mac OS X ([\d_.]+)/ },
  { name: 'iOS', regex: /iPhone OS ([\d_]+)/ },
  { name: 'Android', regex: /Android ([\d.]+)/ },
  { name: 'Linux', regex: /Linux/ },
];

// Transforme le user-agent brut en "Chrome 131 sur macOS 14.6" plutôt que de laisser
// la chaîne illisible à interpréter par un humain.
const parseUserAgent = (userAgent) => {
  if (!userAgent) {
    return { browser: 'Inconnu', os: 'Inconnu' };
  }
  const browserEntry = BROWSER_PATTERNS.find(({ regex }) => regex.test(userAgent));
  const browserVersion = browserEntry ? userAgent.match(browserEntry.regex)[1] : null;
  const osEntry = OS_PATTERNS.find(({ regex }) => regex.test(userAgent));
  const osVersion = osEntry ? (userAgent.match(osEntry.regex)?.[1] || '').replace(/_/g, '.') : '';

  return {
    browser: browserEntry ? `${browserEntry.name} ${browserVersion}` : 'Inconnu',
    os: osEntry ? `${osEntry.name}${osVersion ? ` ${osVersion}` : ''}` : 'Inconnu',
  };
};

// Ex. "fr-FR" -> "français" : évite d'avoir à connaître les codes ISO par cœur.
const formatLanguageName = (languageCode) => {
  if (!languageCode) {
    return 'Inconnue';
  }
  try {
    const displayName = new Intl.DisplayNames(['fr'], { type: 'language' }).of(languageCode);
    return `${displayName} (${languageCode})`;
  } catch (_error) {
    return languageCode;
  }
};

const formatDuration = (durationMs) => {
  if (!durationMs) {
    return '—';
  }
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const formatDate = (timestamp) => (timestamp ? new Date(timestamp).toLocaleString('fr-FR') : '—');

const EmptyState = ({ label }) => <p className={styles.emptyState}>{label}</p>;

const KeyValueTable = ({ data }) => (
  <table className={styles.table}>
    <tbody>
      {Object.entries(data || {}).map(([key, value]) => (
        <tr key={key}>
          <th className={styles.tableKey}>{key}</th>
          <td className={styles.tableValue}>{Array.isArray(value) ? value.join(', ') : String(value)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SummaryTab = ({ session }) => {
  const { browser, os } = parseUserAgent(session.environment?.userAgent);

  return (
    <div className={styles.tabContent}>
      <h3 className={styles.sectionTitle}>Session</h3>
      <KeyValueTable
        data={{
          démarré: formatDate(session.meta?.startedAt),
          arrêté: formatDate(session.meta?.stoppedAt),
          durée: formatDuration(session.meta?.durationMs),
        }}
      />
      <h3 className={styles.sectionTitle}>Environnement</h3>
      <KeyValueTable
        data={{
          navigateur: browser,
          système: os,
          langue: formatLanguageName(session.environment?.language),
          connexion: session.environment?.online ? 'En ligne' : 'Hors ligne',
          écran: `${session.environment?.viewport?.width || '?'} × ${session.environment?.viewport?.height || '?'} px (ratio ${session.environment?.devicePixelRatio || '?'})`,
          provenance: session.environment?.referrer || 'Accès direct (pas de referrer)',
        }}
      />
      <h3 className={styles.sectionTitle}>Utilisateur</h3>
      {session.userInfo ? <KeyValueTable data={session.userInfo} /> : <EmptyState label="Non connecté" />}
      <h3 className={styles.sectionTitle}>Consentement cookies</h3>
      <p className={styles.pre}>{session.consent || 'Aucun'}</p>
    </div>
  );
};

const PageTab = ({ session }) => (
  <div className={styles.tabContent}>
    <h3 className={styles.sectionTitle}>URL</h3>
    <p>{session.currentPage?.path || '—'}</p>
    <h3 className={styles.sectionTitle}>Contexte de page</h3>
    <DebugJsonTree data={session.currentPage?.pageContext} label="pageContext" />
    <h3 className={styles.sectionTitle}>Données GraphQL</h3>
    <DebugJsonTree data={session.currentPage?.data} label="data" />
  </div>
);

const NavigationTab = ({ session }) => {
  const entries = session.navigationLog || [];
  if (!entries.length) {
    return <EmptyState label="Aucune navigation enregistrée" />;
  }
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Heure</th>
          <th>Chemin</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => (
          <tr key={index}>
            <td>{formatDate(entry.timestamp)}</td>
            <td>{entry.path}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const LogsTab = ({ session }) => {
  const entries = session.eventLog || [];
  if (!entries.length) {
    return <EmptyState label="Aucune erreur ou warning enregistré" />;
  }
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Heure</th>
          <th>Niveau</th>
          <th>Message</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => (
          <tr key={index}>
            <td>{formatDate(entry.timestamp)}</td>
            <td>
              <span className={clsx(styles.badge, entry.level === 'error' ? styles.badgeError : styles.badgeWarn)}>
                {entry.level}
              </span>
            </td>
            <td>
              <p>{entry.message}</p>
              {entry.stack && <pre className={styles.pre}>{entry.stack}</pre>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const NetworkTab = ({ session }) => {
  const entries = session.networkLog || [];
  if (!entries.length) {
    return <EmptyState label="Aucune requête en échec enregistrée" />;
  }
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Heure</th>
          <th>Méthode</th>
          <th>Statut</th>
          <th>URL</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => (
          <tr key={index}>
            <td>{formatDate(entry.timestamp)}</td>
            <td>{entry.method}</td>
            <td>{entry.status}</td>
            <td className={styles.tableUrl}>{entry.url}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const TAB_CONTENT = {
  summary: SummaryTab,
  page: PageTab,
  navigation: NavigationTab,
  logs: LogsTab,
  network: NetworkTab,
};

const DebugViewer = ({ initialSession = null }) => {
  const [session, setSession] = useState(initialSession);
  const [activeTab, setActiveTab] = useState('summary');
  const [error, setError] = useState(null);

  const loadFile = (file) => {
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        setSession(JSON.parse(reader.result));
        setActiveTab('summary');
        setError(null);
      } catch (_parseError) {
        setError('Fichier JSON invalide.');
      }
    };
    reader.readAsText(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    loadFile(event.dataTransfer.files?.[0]);
  };

  if (!session) {
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: zone de drop de fichier, l'input file reste le moyen accessible au clavier
      <div className={styles.dropZone} onDragOver={(event) => event.preventDefault()} onDrop={handleDrop}>
        <p>Déposez un fichier debug-session-*.json ici, ou sélectionnez-le</p>
        <input accept="application/json" onChange={(event) => loadFile(event.target.files?.[0])} type="file" />
        {error && <p className={styles.badgeError}>{error}</p>}
      </div>
    );
  }

  const ActiveTabComponent = TAB_CONTENT[activeTab];

  return (
    <div className={styles.viewer}>
      <div className={styles.header}>
        <span className={styles.title}>Debug Viewer</span>
        <button className={styles.resetButton} onClick={() => setSession(null)} type="button">
          Charger un autre fichier
        </button>
      </div>
      <div className={styles.tablist} role="tablist">
        {TABS.map((tab) => (
          <button
            aria-selected={activeTab === tab.key}
            className={clsx(styles.tab, activeTab === tab.key && styles.tabActive)}
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={styles.tabPanel} role="tabpanel">
        <ActiveTabComponent session={session} />
      </div>
    </div>
  );
};

DebugViewer.propTypes = {
  initialSession: PropTypes.object,
};

export default DebugViewer;
